import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Input, Textarea, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError,
  LoadingButton, Amount, RowAction,
} from '@redpanda/ui';
import { formatTableDate, useUnsavedChangesWarning } from '@redpanda/utils';
import { createInvoice, updateInvoice, deleteInvoicePayment, deleteCreditNote } from '../services/api';
import { Invoice, isInvoiceLimited, useInvoiceStoreBase, updateInvoiceInLists } from '../stores/invoiceStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { dateInputValue, todayInput, toUtcMidnight } from '../utils/dates';
import { useCrmCustomers } from '../stores/lookupStore';
import { useAccountOptionsStore } from '../stores/accountStore';

interface InvoiceDialogProps {
  /** 有值＝編輯；沒有＝新增 */
  invoice?: Invoice;
  /** 只能查看（已收款、已作廢、已封存的發票） */
  readOnly?: boolean;
  onClose: () => void;
  /** 新增成功後（重新整理方案用量） */
  onCreated?: () => void;
}

interface ItemForm {
  description: string;
  quantity: string;
  unitPrice: string;
}

interface FormData {
  number: string;
  date: string;
  dueDate: string;
  customerName: string;
  customerTaxId: string;
  customerAddress: string;
  revenueAccountId: string;
  taxRate: string;
  notes: string;
  items: ItemForm[];
}

const TAX_RATES = ['0.05', '0'];
const emptyItem = (): ItemForm => ({ description: '', quantity: '1', unitPrice: '' });
const emptyForm = (): FormData => ({
  number: '', date: todayInput(), dueDate: '', customerName: '', customerTaxId: '', customerAddress: '', revenueAccountId: '', taxRate: '0.05', notes: '',
  items: [emptyItem()],
});
const num = (value: string) => Number(value) || 0;
const sanitizeDecimal = (value: string) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');

/**
 * 新增／編輯發票：等伺服器回應才關（§3-17）
 * 發票號碼重複、超過方案每月發票數都要由後端判斷；號碼重複時標在欄位下方
 */
const InvoiceDialog: React.FC<InvoiceDialogProps> = ({ invoice, readOnly = false, onClose, onCreated }) => {
  const { t, i18n } = useTranslation();
  const isEditMode = !!invoice;
  // 已收款、有收款或折讓：只能改到期日、地址、備註（金額、客戶、品項鎖住）
  const limited = !readOnly && !!invoice && isInvoiceLimited(invoice);
  const lockFinancial = readOnly || limited;
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, formState: { errors, isDirty }, reset, control, watch, setValue, setError } = useForm<FormData>({
    defaultValues: emptyForm(),
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  // 收入科目選單（收入類、依代號）；留空＝4101 營業收入
  const incomeAccounts = useAccountOptionsStore(state => state.accounts).filter(account => account.type === 'INCOME');
  const fetchAccounts = useAccountOptionsStore(state => state.fetchAll);
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);
  // 刪除收款、折讓：後端連同銀行交易、分錄一起刪，發票狀態可能從已收款改回已開立 → 刪完關閉對話框
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'payment' | 'creditNote'; id: string } | null>(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const handleDeleteRecord = async () => {
    if (!invoice || !deleteTarget) return;
    setDeletingRecord(true);
    try {
      const { data: saved } = deleteTarget.kind === 'payment'
        ? await deleteInvoicePayment(invoice.id, deleteTarget.id)
        : await deleteCreditNote(invoice.id, deleteTarget.id);
      updateInvoiceInLists(invoice.id, saved);
      toast.success(t(deleteTarget.kind === 'payment' ? 'invoices.payment.deleted' : 'invoices.creditNote.deleted'));
      notifyDataChanged('invoices');
      notifyDataChanged('bankTransactions');
      notifyDataChanged('journals');
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.deleteFailed'));
    } finally {
      setDeletingRecord(false);
      setDeleteTarget(null);
    }
  };
  const canDeleteRecords = !!invoice && invoice.status !== 'CANCELLED' && !invoice.archived;
  // CRM 客戶（R38）：選了帶入名稱（有公司名用公司名）、地址；發票存當時的副本，之後 CRM 改名不影響已開的發票
  const crmCustomers = useCrmCustomers();
  const [crmCustomerId, setCrmCustomerId] = useState('');
  const pickCrmCustomer = (id: string) => {
    setCrmCustomerId(id);
    const customer = crmCustomers.find(c => c.id === id);
    if (!customer) return;
    setValue('customerName', customer.company || customer.name, { shouldDirty: true, shouldValidate: true });
    if (customer.address) setValue('customerAddress', customer.address, { shouldDirty: true });
  };

  // 初始值在繪製前帶入（R31）
  useLayoutEffect(() => {
    reset(invoice ? {
      number: invoice.number,
      date: dateInputValue(invoice.date),
      dueDate: dateInputValue(invoice.dueDate),
      customerName: invoice.customerName,
      customerTaxId: invoice.customerTaxId || '',
      customerAddress: invoice.customerAddress || '',
      revenueAccountId: invoice.revenueAccountId || '',
      taxRate: Number(invoice.taxRate) === 0 ? '0' : '0.05',
      notes: invoice.notes || '',
      items: invoice.items.length > 0
        ? invoice.items.map(item => ({ description: item.description, quantity: String(Number(item.quantity)), unitPrice: String(Number(item.unitPrice)) }))
        : [emptyItem()],
    } : emptyForm());
  }, [invoice, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !saving && !readOnly, onClose);

  const items = watch('items');
  const taxRate = num(watch('taxRate'));
  const subtotal = Math.round(items.reduce((sum, item) => sum + num(item.quantity) * num(item.unitPrice), 0) * 100) / 100;
  const taxAmount = Math.round(subtotal * taxRate * 100) / 100;

  const onSubmit = async (data: FormData) => {
    const fullPayload = {
      number: data.number.trim(),
      date: toUtcMidnight(data.date),
      dueDate: data.dueDate ? toUtcMidnight(data.dueDate) : null,
      customerName: data.customerName.trim(),
      customerTaxId: data.customerTaxId.trim() || null,
      customerAddress: data.customerAddress.trim() || null,
      revenueAccountId: data.revenueAccountId || null,
      taxRate: num(data.taxRate),
      notes: data.notes.trim() || null,
      items: data.items.map(item => ({ description: item.description.trim(), quantity: num(item.quantity), unitPrice: num(item.unitPrice) })),
    };
    // 只能改非金額欄位時只送這三個（後端擋其他欄位）
    const payload = limited
      ? { dueDate: fullPayload.dueDate, customerAddress: fullPayload.customerAddress, notes: fullPayload.notes }
      : fullPayload;
    setSaving(true);
    try {
      if (invoice) {
        const { data: saved } = await updateInvoice(invoice.id, payload);
        updateInvoiceInLists(invoice.id, saved);
        toast.success(t('common.messages.updateSuccess'));
      } else {
        const { data: saved } = await createInvoice(payload);
        useInvoiceStoreBase.getState().addItem(saved);
        toast.success(t('common.messages.createSuccess'));
        onCreated?.();
      }
      notifyDataChanged('invoices');
      onClose();
    } catch (error: any) {
      if (error?.response?.data?.code === 'INVOICE_NUMBER_EXISTS') {
        setError('number', { type: 'manual', message: t('errors.INVOICE_NUMBER_EXISTS') });
      } else {
        toast.error(apiErrorMessage(error, invoice ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const required = { validate: (value: string) => !!value.trim() || t('common.forms.validation.required') };
  const decimalField = (index: number, name: 'quantity' | 'unitPrice') => {
    const field = register(`items.${index}.${name}` as const, {
      validate: value => num(value) > 0 || t(name === 'quantity' ? 'common.form.minQuantity' : 'common.form.minUnitPrice'),
    });
    return (
      <Input
        {...field}
        inputMode="decimal"
        aria-label={t(name === 'quantity' ? 'invoices.quantity' : 'invoices.unitPrice')}
        className="text-right tabular-nums"
        variant={errors.items?.[index]?.[name] ? 'error' : 'default'}
        onChange={event => {
          event.target.value = sanitizeDecimal(event.target.value);
          return field.onChange(event);
        }}
      />
    );
  };
  const itemGrid = 'grid grid-cols-[minmax(0,1fr)_5.5rem_8rem_8rem_2rem] gap-3';

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={saving} className="max-w-4xl">
        <DialogHeader
          title={readOnly ? t('invoices.view') : isEditMode ? t('invoices.edit') : t('invoices.add')}
          onClose={handleClose}
          closeDisabled={saving}
          closeLabel={t('common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* fieldset disabled：查看模式下所有欄位一次停用 */}
          <fieldset disabled={readOnly} className="p-6 space-y-5 min-w-0">
            {readOnly && <p className="text-sm text-fg-secondary">{t('invoices.dialog.lockedHint')}</p>}
            {limited && <p className="text-sm text-fg-secondary">{t('invoices.dialog.limitedHint')}</p>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormFieldWithError label={t('invoices.number')} required={isEditMode} error={errors.number?.message} htmlFor="number">
                <Input
                  id="number"
                  autoFocus={!isEditMode}
                  disabled={limited}
                  placeholder={isEditMode ? undefined : t('invoices.dialog.autoNumber')}
                  {...register('number', isEditMode ? required : undefined)}
                  variant={errors.number ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('invoices.date')} required error={errors.date?.message} htmlFor="date">
                <Input id="date" type="date" disabled={limited} {...register('date', { required: t('common.forms.validation.required') })} variant={errors.date ? 'error' : 'default'} />
              </FormFieldWithError>
              <FormFieldWithError label={t('invoices.dueDate')} error={errors.dueDate?.message} htmlFor="dueDate">
                <Input
                  id="dueDate"
                  type="date"
                  {...register('dueDate', { validate: value => !value || value >= watch('date') || t('invoices.dialog.dueBeforeDate') })}
                  variant={errors.dueDate ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            {!lockFinancial && crmCustomers.length > 0 && (
              <FormFieldWithError label={t('invoices.dialog.crmCustomer')} htmlFor="crmCustomer">
                <CustomSelect
                  id="crmCustomer"
                  value={crmCustomerId}
                  onChange={pickCrmCustomer}
                  placeholder={t('invoices.dialog.crmCustomerPlaceholder')}
                  searchable
                  options={crmCustomers.map(c => ({ value: c.id, label: c.company && c.company !== c.name ? `${c.company} · ${c.name}` : c.name }))}
                />
              </FormFieldWithError>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <FormFieldWithError label={t('invoices.customerName')} required error={errors.customerName?.message} htmlFor="customerName">
                  <Input id="customerName" disabled={limited} {...register('customerName', required)} variant={errors.customerName ? 'error' : 'default'} />
                </FormFieldWithError>
              </div>
              <FormFieldWithError label={t('invoices.customerTaxId')} error={errors.customerTaxId?.message} htmlFor="customerTaxId">
                <Input
                  id="customerTaxId"
                  disabled={limited}
                  inputMode="numeric"
                  {...register('customerTaxId', { validate: value => !value.trim() || /^\d{8}$/.test(value.trim()) || t('invoices.dialog.taxIdFormat') })}
                  variant={errors.customerTaxId ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <FormFieldWithError label={t('invoices.address')} htmlFor="customerAddress">
                  <Input id="customerAddress" {...register('customerAddress')} />
                </FormFieldWithError>
              </div>
              <FormFieldWithError label={t('invoices.taxRate')} htmlFor="taxRate">
                <CustomSelect
                  id="taxRate"
                  value={watch('taxRate')}
                  onChange={value => setValue('taxRate', value, { shouldDirty: true })}
                  disabled={lockFinancial}
                  options={TAX_RATES.map(rate => ({ value: rate, label: t(rate === '0' ? 'invoices.dialog.taxFree' : 'invoices.dialog.taxStandard') }))}
                />
              </FormFieldWithError>
            </div>

            <FormFieldWithError label={t('invoices.dialog.revenueAccount')} helpText={t('invoices.dialog.revenueAccountHint')} htmlFor="revenueAccountId">
              <CustomSelect
                id="revenueAccountId"
                value={watch('revenueAccountId')}
                onChange={value => setValue('revenueAccountId', value, { shouldDirty: true })}
                disabled={lockFinancial}
                options={[
                  { value: '', label: t('invoices.dialog.defaultRevenueAccount') },
                  ...incomeAccounts.map(account => ({ value: account.id, label: `${account.code} ${account.name}` })),
                ]}
              />
            </FormFieldWithError>

            {/* 品項：金額靠右（§3-2）；小計、稅額、總額由後端依明細重算，這裡只是預覽；只能改非金額欄位時整塊停用 */}
            <fieldset disabled={limited} className="min-w-0">
              <div className="overflow-x-auto">
                <div className="min-w-[600px]">
                  <div className={`${itemGrid} pb-2 text-sm font-medium text-fg-secondary`}>
                    <span>{t('invoices.itemDescription')} <span className="text-red-500">*</span></span>
                    <span className="text-right">{t('invoices.quantity')}</span>
                    <span className="text-right">{t('invoices.unitPrice')}</span>
                    <span className="text-right">{t('invoices.itemAmount')}</span>
                    <span />
                  </div>
                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <div key={field.id} className={`${itemGrid} items-center`}>
                        <Input
                          aria-label={t('invoices.itemDescription')}
                          {...register(`items.${index}.description` as const, required)}
                          variant={errors.items?.[index]?.description ? 'error' : 'default'}
                        />
                        {decimalField(index, 'quantity')}
                        {decimalField(index, 'unitPrice')}
                        <span className="text-right text-sm text-fg">
                          <Amount value={num(items[index]?.quantity) * num(items[index]?.unitPrice)} />
                        </span>
                        {fields.length > 1 && !lockFinancial ? (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="h-10 w-8 inline-flex items-center justify-center text-fg-secondary hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            aria-label={t('invoices.dialog.removeItem')}
                            title={t('invoices.dialog.removeItem')}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        ) : <span />}
                      </div>
                    ))}
                  </div>
                  <div className={lockFinancial ? 'hidden' : 'mt-2'}>
                    <button
                      type="button"
                      onClick={() => append(emptyItem())}
                      className="inline-flex items-center gap-1 h-8 text-sm font-medium text-accent hover:text-brand-700 dark:hover:text-brand-300"
                    >
                      <PlusIcon className="h-4 w-4" />
                      {t('invoices.addItem')}
                    </button>
                  </div>
                </div>
              </div>

              <dl className="mt-3 ml-auto w-full max-w-xs space-y-1.5 border-t border-line pt-3 text-sm">
                <div className="flex justify-between"><dt className="text-fg-secondary">{t('invoices.itemSubtotal')}</dt><dd><Amount value={subtotal} /></dd></div>
                <div className="flex justify-between"><dt className="text-fg-secondary">{t('invoices.taxAmount')}</dt><dd><Amount value={taxAmount} /></dd></div>
                <div className="flex justify-between font-semibold text-fg"><dt>{t('invoices.totalAmount')}</dt><dd><Amount value={subtotal + taxAmount} /></dd></div>
              </dl>
            </fieldset>

            <FormFieldWithError label={t('invoices.notes')} htmlFor="notes">
              <Textarea id="notes" rows={2} {...register('notes')} />
            </FormFieldWithError>
          </fieldset>

          {/* 收款記錄（fieldset 外：唯讀時也能刪除收款） */}
          {invoice && (invoice.payments?.length ?? 0) > 0 && (
            <section className="px-6 pb-6">
              <h3 className="mb-2 text-sm font-medium text-fg-secondary">{t('invoices.payment.history')}</h3>
              <ul className="divide-y divide-line rounded-lg border border-line">
                {invoice.payments!.map(payment => (
                  <li key={payment.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="w-24 tabular-nums text-fg-secondary">{formatTableDate(payment.date, i18n.language)}</span>
                    <span className="flex-1 min-w-0 truncate text-fg">{payment.bankAccount ? `${payment.bankAccount.accountName}（${payment.bankAccount.bankName}）` : '—'}</span>
                    <span className="font-medium text-fg"><Amount value={Number(payment.amount)} /></span>
                    {canDeleteRecords && (
                      <RowAction tone="danger" onClick={() => setDeleteTarget({ kind: 'payment', id: payment.id })}>{t('common.actions.delete')}</RowAction>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 折讓記錄 */}
          {invoice && (invoice.creditNotes?.length ?? 0) > 0 && (
            <section className="px-6 pb-6">
              <h3 className="mb-2 text-sm font-medium text-fg-secondary">{t('invoices.creditNote.history')}</h3>
              <ul className="divide-y divide-line rounded-lg border border-line">
                {invoice.creditNotes!.map(note => (
                  <li key={note.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="w-24 tabular-nums text-fg-secondary">{formatTableDate(note.date, i18n.language)}</span>
                    <span className="w-24 tabular-nums text-fg">{note.number}</span>
                    <span className="flex-1 min-w-0 truncate text-fg-secondary" title={note.reason ?? undefined}>
                      {note.reason || '—'}
                      {Number(note.refundAmount) > 0 && <span className="ml-2">{t('invoices.creditNote.refunded')} <Amount value={Number(note.refundAmount)} /></span>}
                    </span>
                    <span className="font-medium text-fg"><Amount value={Number(note.totalAmount)} /></span>
                    {canDeleteRecords && (
                      <RowAction tone="danger" onClick={() => setDeleteTarget({ kind: 'creditNote', id: note.id })}>{t('common.actions.delete')}</RowAction>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={saving} className={dialogButtonClass.cancel}>
              {readOnly ? t('common.actions.close') : t('common.actions.cancel')}
            </button>
            {!readOnly && (
              <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
                {isEditMode ? t('common.actions.save') : t('common.actions.create')}
              </LoadingButton>
            )}
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteRecord}
        title={t(deleteTarget?.kind === 'creditNote' ? 'invoices.creditNote.deleteTitle' : 'invoices.payment.deleteTitle')}
        message={t(deleteTarget?.kind === 'creditNote' ? 'invoices.creditNote.deleteMessage' : 'invoices.payment.deleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={deletingRecord}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default InvoiceDialog;
